import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import wordDictionary from '../services/WordDictionary.js';

/**
 * Language Context
 * Manages current language state and provides language-specific utilities
 * Also synchronizes the WordDictionary service with the current language
 */
const LanguageContext = createContext();

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export function LanguageProvider({ children }) {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');
  const [isDictionaryChanging, setIsDictionaryChanging] = useState(false);

  // Initialize WordDictionary with the current language on mount
  useEffect(() => {
    const initializeDictionary = async () => {
      const initialLanguage = i18n.language || 'en';
      console.log('🌍 Initializing WordDictionary with language:', initialLanguage);

      if (wordDictionary.language !== initialLanguage) {
        setIsDictionaryChanging(true);
        try {
          await wordDictionary.changeLanguage(initialLanguage);
          console.log('✅ WordDictionary initialized with language:', initialLanguage);
        } catch (error) {
          console.error('❌ Failed to initialize WordDictionary:', error);
        } finally {
          setIsDictionaryChanging(false);
        }
      }
    };

    initializeDictionary();
  }, []); // Run once on mount

  // Listen for language changes from i18next and update WordDictionary
  useEffect(() => {
    const handleLanguageChange = async (lng) => {
      setCurrentLanguage(lng);
      console.log('🌍 Language changed to:', lng);

      // Update WordDictionary to use the new language
      if (wordDictionary.language !== lng) {
        setIsDictionaryChanging(true);
        try {
          console.log('📚 Changing WordDictionary language to:', lng);
          await wordDictionary.changeLanguage(lng);
          console.log('✅ WordDictionary language changed successfully to:', lng);
        } catch (error) {
          console.error('❌ Failed to change WordDictionary language:', error);
        } finally {
          setIsDictionaryChanging(false);
        }
      }
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const value = {
    currentLanguage,
    isEnglish: currentLanguage === 'en',
    isDanish: currentLanguage === 'da',
    isDictionaryChanging,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

